"use client";
import { htmlDataParserUtilsV2 } from "@/apiUtils/htmlDataPerserUtils";
import { TranslationData } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { processCmsText } from "@/utils/textProcessing";
import * as React from "react";
import { FC, useMemo, useState } from "react";
import root from "react-shadow";
import { replaceCurrencyPlaceholderInHtml } from "@/apiUtils/commonUtils";
import { useParams } from "next/navigation";
interface AllViewExpandableContentProps {
  text: string;
  automationPayload?: string[];
  translations?: TranslationData;
  locale?: string;
  customTextClassName?: string;
  customButtonClassName?: string;
}

interface ExpandableContentProps {
  minView: string; // Type for minView (HTML content as string)
  totalView: string; // Type for totalView (HTML content as string)
  automationPayload?: string[]; // Type for the automation payload
  translations?: TranslationData;
  customTextClassName?: string;
  customButtonClassName?: string;
}

export const ExpandableContent: FC<ExpandableContentProps> = (props) => {
  const { minView, totalView, automationPayload, translations, customTextClassName, customButtonClassName } = props;
  const hasSecondarySection = totalView.length > minView.length;
  const [collapse, setCollapse] = useState(hasSecondarySection);

  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  // Ensure proper HTML entity handling
  const sanitizeHtml = (html: string) => {
    if (!html) return "";
    // Preserve all valid HTML entities and special characters
    return html;
  };

  return (
    <div
      className={cn(
        customTextClassName || "max-w-[72rem] text-center text-base md:text-lg font-normal leading-[100%] tracking-negative text-tertiary-6",
      )}
    >
      <root.span>
        <style>
          {`
            p {
                margin: 0;
                padding: 0;
                font-size: inherit;
              }
            span {
                font-size: inherit;
              }
            a {
                color:#666666;
              }
            .dirham-symbol {
              font-family: var(--font-currency-aed, sans-serif);
              font-size: 0.75em !important;
            }
            .riyal-symbol {
              font-family: var(--font-currency-sar, sans-serif);
              font-size: 0.75em !important;
            }
          `}
        </style>

        <span
          className="inline"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(collapse ? minView : totalView)
          }}
        />
      </root.span>
      {hasSecondarySection && (
        <>
          {collapse && <span>...</span>}
          <button
            onClick={handleCollapse}
            className={customButtonClassName || "ml-1 cursor-pointer text-[0.875rem] font-semibold text-tertiary-6 underline underline-offset-2 sm:ml-[2px] sm:text-[1.125rem]"}
          >
            {collapse
              ? (translations?.common?.common_read_more_label ?? "Read More")
              : (translations?.common?.common_show_less_label ?? "Show Less")}
          </button>
        </>
      )}
    </div>
  );
};

const AllViewExpandableContent: FC<AllViewExpandableContentProps> = ({
  text,
  automationPayload,
  translations,
  locale,
  customTextClassName,
  customButtonClassName,
}) => {
  const { paramLocale } = useParams();
  const processedText = useMemo(() => {
     return replaceCurrencyPlaceholderInHtml(text, locale || (paramLocale as string));
  }, [text, locale]);

  const [minViewForMobile, minViewForDesktop, totalView] = useMemo(() => {
    const BREAK_POINTS: number[] = [125, 380];
    return htmlDataParserUtilsV2(processedText, BREAK_POINTS[0], BREAK_POINTS[1]);
  }, [processedText]);

  return (
    <>
      <div className="md:hidden">
        <ExpandableContent
          minView={minViewForMobile}
          totalView={totalView}
          automationPayload={automationPayload}
          translations={translations}
          customTextClassName={customTextClassName}
          customButtonClassName={customButtonClassName}
        />
      </div>
      <div className="hidden md:block">
        <ExpandableContent
          minView={minViewForDesktop}
          totalView={totalView}
          automationPayload={automationPayload}
          translations={translations}
          customTextClassName={customTextClassName}
          customButtonClassName={customButtonClassName}
        />
      </div>
      {/* <p className="visually-hidden" dangerouslySetInnerHTML={{ __html: totalView }} /> */}
    </>
  );
};

export default AllViewExpandableContent;